'use client';
import { useState, useEffect, useCallback, useRef } from 'react';

const SWIPE_THRESHOLD_PX = 50;

/**
 * Shared carousel state and interaction logic.
 */
export const useCarousel = ({
  count,
  autoplayEnabled = true,
  autoplayIntervalMs = 4000,
  transitionDurationMs = 640,
  transitionEasing = 'cubic-bezier(0.76, 0, 0.24, 1)',
  resumeAfterMs = 8000,
  pauseOnHover = true,
  showArrowControls = true,
  showDotIndicators = false,
}) => {
  const [slideState, setSlideState] = useState({
    activeIndex: 0,
    outgoingIndex: null,
    direction: 'next',
  });
  const [hovering, setHovering] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const animGenRef = useRef(0);
  const resumeTimerRef = useRef(undefined);
  const touchStartXRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = e => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const effectiveTransitionMs = reducedMotion ? 0 : transitionDurationMs;

  const clearOutgoing = useCallback(
    gen => {
      if (effectiveTransitionMs > 0) {
        setTimeout(() => {
          if (animGenRef.current === gen) {
            setSlideState(prev => ({ ...prev, outgoingIndex: null }));
          }
        }, effectiveTransitionMs + 50);
      } else {
        setSlideState(prev => ({ ...prev, outgoingIndex: null }));
      }
    },
    [effectiveTransitionMs],
  );

  const navigate = useCallback(
    dir => {
      if (count <= 1) return;
      const gen = ++animGenRef.current;
      setSlideState(prev => {
        const next =
          dir === 'next'
            ? (prev.activeIndex + 1) % count
            : (prev.activeIndex - 1 + count) % count;
        return {
          activeIndex: next,
          outgoingIndex: prev.activeIndex,
          direction: dir,
        };
      });
      clearOutgoing(gen);
    },
    [count, clearOutgoing],
  );

  const scheduleResume = useCallback(() => {
    clearTimeout(resumeTimerRef.current);
    setUserPaused(true);
    resumeTimerRef.current = setTimeout(
      () => setUserPaused(false),
      resumeAfterMs,
    );
  }, [resumeAfterMs]);

  const goNext = useCallback(() => {
    navigate('next');
    scheduleResume();
  }, [navigate, scheduleResume]);

  const goPrev = useCallback(() => {
    navigate('prev');
    scheduleResume();
  }, [navigate, scheduleResume]);

  const goTo = useCallback(
    index => {
      if (index === slideState.activeIndex || index < 0 || index >= count)
        return;
      const dir = index > slideState.activeIndex ? 'next' : 'prev';
      const gen = ++animGenRef.current;
      setSlideState(prev => ({
        activeIndex: index,
        outgoingIndex: prev.activeIndex,
        direction: dir,
      }));
      clearOutgoing(gen);
      scheduleResume();
    },
    [slideState.activeIndex, count, clearOutgoing, scheduleResume],
  );

  const isPlaying = autoplayEnabled && !hovering && !userPaused && count > 1;

  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => navigate('next'), autoplayIntervalMs);
    return () => clearInterval(timer);
  }, [isPlaying, autoplayIntervalMs, navigate]);

  useEffect(() => {
    return () => clearTimeout(resumeTimerRef.current);
  }, []);

  const handlers = {
    onMouseEnter: () => {
      if (pauseOnHover) setHovering(true);
    },
    onMouseLeave: () => {
      if (pauseOnHover) setHovering(false);
    },
    onTouchStart: e => {
      touchStartXRef.current = e.touches[0]?.clientX ?? null;
    },
    onTouchEnd: e => {
      if (touchStartXRef.current === null) return;
      const delta =
        (e.changedTouches[0]?.clientX ?? touchStartXRef.current) -
        touchStartXRef.current;
      touchStartXRef.current = null;
      if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
      if (delta < 0) goNext();
      else goPrev();
    },
  };

  return {
    activeIndex: slideState.activeIndex,
    outgoingIndex: slideState.outgoingIndex,
    direction: slideState.direction,
    goNext,
    goPrev,
    goTo,
    isPlaying,
    reducedMotion,
    animationConfig: {
      durationMs: effectiveTransitionMs,
      easing: transitionEasing,
    },
    arrowsVisible: showArrowControls && count > 1,
    dotsVisible: showDotIndicators && count > 1,
    handlers,
  };
};
